<template>
  <div>
    <div :class="$style['hero']">
      <h1>{{ $t('account.pages.subPageRepayments.paymentSchedule') }}</h1>
      <div v-if="!this.isSummaryLoading">
        <DSButton
          v-if="disableInvoiceUpload"
          :label="waitlistCtaLabel"
          @click="$emit('disable-invoice-upload-modal')"
        />
        <DSButton
          v-else-if="!showSummary && userCanSubmitInvoices"
          :disabled="userCanSubmitInvoices"
          :label="payAnInvoiceModalButtonText"
          @click="$emit('pay-an-invoice')"
        />
        <DSButton
          v-else-if="!isBusinessProfileComplete"
          label="Finish your application"
          @click="handleFinishApplicationButtonClick"
        />
        <PaymentTransactionsSummary v-else :summary="transactionsSummary" />
      </div>
    </div>
    <section :class="$style.section">
      <PaymentNav />
      <PaymentVendorsTable
        v-if="Object.keys(filters).length > 0"
        :repayments="filteredRepayments.transactions || []"
        :repayments-type="repaymentsType"
        :filters="filters"
        :is-loading="isLoading"
        :selected-filters="selectedFilters"
        @on-selected-filters="updateSelectedFilters"
      />
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Subtype } from '@clearbanc/data-common-types';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { RouteNames } from '@/data/routes';

import DSButton from 'primevue/button';
import PaymentTransactionsSummary from '@/pages/payments/components/PaymentTransactionsSummary.vue';
import PaymentNav from '@/pages/payments/components/PaymentNav.vue';
import PaymentVendorsTable from '@/pages/payments/components/PaymentVendorsTable.vue';

export default {
  components: {
    DSButton,
    PaymentNav,
    PaymentVendorsTable,
    PaymentTransactionsSummary,
  },
  props: {
    repaymentsType: {
      validator(value) {
        return ['history', 'upcoming'].includes(value);
      },
    },
  },
  data() {
    const repaymentsType = this.repaymentsType;
    return {
      selectedFilters: {
        invoiceIds: [],
        monthYears: [],
        vendors: [],
        billTypes: [],
        ...(repaymentsType === 'upcoming' ? { dueDate: 0 } : { statuses: [] }),
      },
      isLoading: true,
      isSummaryLoading: true,
      filters: {},
    };
  },
  async mounted() {
    await this.$store.dispatchApiAction('GET_TRANSACTIONS_SUMMARY', {
      businessId: this.businessId,
    });

    await this.$store.dispatchApiAction('GET_TRANSACTIONS_WITH_FILTERS', {
      businessId: this.businessId,
      filterBy: this.selectedFilters,
      repaymentsType: this.repaymentsType,
      subtype: Subtype.BILLS,
    });
    this.isLoading = false;
    this.isSummaryLoading = false;
    this.filters = { ...this.filteredRepayments.filters };
  },
  computed: {
    ...mapGetters([
      'businessId',
      'filteredRepayments',
      'transactionsSummary',
      'isBnplWaitlisted',
      'userCanSubmitInvoices',
      'disableInvoiceUpload',
      'isBusinessProfileComplete',
      'business',
    ]),
    showSummary() {
      return Number(this.transactionsSummary.outstandingBalance) > 0;
    },
    payAnInvoiceModalButtonText() {
      return this.isBnplWaitlisted
        ? this.$t('account.pages.pageVendors.joinTheWaitlist')
        : this.$t('account.pages.pageVendors.accessWorkingCapital');
    },
    waitlistCtaLabel() {
      return this.$t('account.pages.pageVendors.joinTheWaitlist');
    },
  },
  methods: {
    async updateSelectedFilters(evt) {
      this.selectedFilters = evt;
      this.isLoading = true;

      await this.$store.dispatch('RESET_FILTERED_REPAYMENTS');
      await this.$store.dispatchApiAction('GET_TRANSACTIONS_WITH_FILTERS', {
        businessId: this.businessId,
        filterBy: this.selectedFilters,
        subtype: Subtype.BILLS,
        repaymentsType: this.repaymentsType,
      });
      this.isLoading = false;
    },
    handleFinishApplicationButtonClick() {
      analytics.track(SEGMENT_EVENTS.finish_application_cta_clicked, {
        businessId: this.businessId,
        businessName: this.business.name,
      });
      this.$router.push({
        name: RouteNames.PROFILE.INDEX,
      });
    },
  },
  watch: {
    repaymentsType: {
      async handler() {
        // reset filters and reload the transactions

        const repaymentsType = this.repaymentsType;

        await this.updateSelectedFilters({
          invoiceIds: [],
          monthYears: [],
          vendors: [],
          billTypes: [],
          ...(repaymentsType === 'upcoming'
            ? { dueDate: 0 }
            : { statuses: [] }),
        });

        this.filters = { ...this.filteredRepayments.filters };
      },
    },
  },
};
</script>

<style lang="less" module>
.hero {
  margin: 24px 0;

  h1 {
    margin-bottom: 22px;
    margin-top: 22px;
  }
}

.section {
  font-family: @regular-font;
  padding: 15px 30px;
  margin-bottom: 25px;
  background: @color-white;
  border: 1px solid @color-lightgray-300;
  box-sizing: border-box;
  border-radius: 4px;
  flex: 1 0 0;
}
</style>
